import { Checkbox, type CheckboxSize } from '@trustoo/components/Checkbox/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const CHECKBOX_USAGE = `import { Checkbox } from '@trustoo/components/Checkbox/v2'

// Uncontrolled
<Checkbox defaultChecked label="Remember me" />

// Controlled
<Checkbox checked={agreed} onChange={setAgreed} label="I agree to the terms" />

// Indeterminate (e.g. a 'select all' that is partially checked)
<Checkbox indeterminate onChange={selectAll}>Select all</Checkbox>

// Sizes + supporting text
<Checkbox
  size="sm"
  label="Email notifications"
  supportingText="Get notified when a new lead matches your profile."
/>`

const StatefulCheckbox: React.FC<{
    children?: React.ReactNode
    label?: React.ReactNode
    supportingText?: React.ReactNode
    size?: 'sm' | 'md'
    initial?: boolean
}> = ({ label, supportingText, size, initial = false }) => {
    const [checked, setChecked] = useState(initial)
    return (
        <Checkbox
            checked={checked}
            onChange={setChecked}
            size={size}
            label={label}
            supportingText={supportingText}
        />
    )
}

const SELECT_ALL_OPTIONS = ['Marketing', 'Design', 'Engineering']

const SelectAllCheckboxDemo: React.FC = () => {
    const [selected, setSelected] = useState<string[]>(['Design'])
    const allChecked = selected.length === SELECT_ALL_OPTIONS.length
    const someChecked = selected.length > 0 && !allChecked

    const toggleAll = () => setSelected(allChecked ? [] : [...SELECT_ALL_OPTIONS])
    const toggleOne = (option: string) =>
        setSelected((prev) =>
            prev.includes(option) ? prev.filter((x) => x !== option) : [...prev, option],
        )

    return (
        <div className="space-y-spacing-lg">
            <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={toggleAll}
                label="Select all"
            />
            <div className="ml-7 flex flex-col gap-spacing-lg">
                {SELECT_ALL_OPTIONS.map((option) => (
                    <Checkbox
                        key={option}
                        checked={selected.includes(option)}
                        onChange={() => toggleOne(option)}
                        label={option}
                    />
                ))}
            </div>
        </div>
    )
}

export const CheckboxShowcase: React.FC = () => (
    <Section
        title="Checkbox (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Checkbox</span> set
                (ComponentSet <InlineCode>5:6526</InlineCode>), scoped to the{' '}
                <InlineCode>Type=Checkbox</InlineCode> variants (the set's{' '}
                <InlineCode>Radio</InlineCode> axis is a separate future component). Import v2
                explicitly via <InlineCode>@trustoo/components/Checkbox/v2</InlineCode> (net-new —
                no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md'] as CheckboxSize[]).map((size) => (
                        <div key={size} className="flex items-center gap-spacing-lg">
                            <Checkbox size={size} defaultChecked />
                            <Checkbox size={size} />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">States</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        Unchecked, checked, <InlineCode>indeterminate</InlineCode> (minus glyph),
                        and <InlineCode>disabled</InlineCode>. Hover and{' '}
                        <InlineCode>:focus-visible</InlineCode> (tab onto a box) are native.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Checkbox />
                    <Checkbox defaultChecked />
                    <Checkbox indeterminate />
                    <Checkbox disabled />
                    <Checkbox disabled defaultChecked />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">
                        Indeterminate (select all)
                    </h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        The canonical use of <InlineCode>indeterminate</InlineCode> — the parent is
                        partially checked when only some children are selected.
                    </p>
                </div>
                <div className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <SelectAllCheckboxDemo />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    With label &amp; supporting text
                </h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <StatefulCheckbox
                        initial
                        label="Remember me"
                        supportingText="Save my login details for next time."
                    />
                    <StatefulCheckbox
                        size="sm"
                        label="Email notifications"
                        supportingText="Get notified when a new lead matches your profile."
                    />
                    <Checkbox
                        disabled
                        defaultChecked
                        label="Two-factor authentication"
                        supportingText="Managed by your organisation."
                    />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={CHECKBOX_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>checked</InlineCode> /{' '}
                    <InlineCode>defaultChecked</InlineCode> + <InlineCode>onChange</InlineCode>{' '}
                    (controlled or uncontrolled), <InlineCode>indeterminate</InlineCode>,{' '}
                    <InlineCode>size</InlineCode> (sm · md), <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>supportingText</InlineCode>, and <InlineCode>disabled</InlineCode>.
                    Renders a native{' '}
                    <InlineCode>&lt;input type=&quot;checkbox&quot;&gt;</InlineCode> (the{' '}
                    <InlineCode>indeterminate</InlineCode> DOM property is set via ref); a provided{' '}
                    <InlineCode>label</InlineCode> is associated via{' '}
                    <InlineCode>htmlFor</InlineCode>. The check / minus glyphs use Material Symbols.
                </p>
            </div>
        </div>
    </Section>
)
